import { useI18n } from '../../../i18n';
import { DataTable, Column, Pagination } from '../../../components/shared';
import { ProductItem, ProductCells } from './ProductTableRow';

interface ProductTableProps {
  data: ProductItem[];
  isLoading?: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onRowClick?: (item: ProductItem) => void;
  onView?: (item: ProductItem) => void;
  onEdit?: (item: ProductItem) => void;
  onDelete?: (item: ProductItem) => void;
  /** Mở màn nhập bản dịch (sheet 2 #15). */
  onTranslations?: (item: ProductItem) => void;
  /**
   * Chọn nhiều dòng để cập nhật hàng loạt.
   *
   * Bỏ trống cả ba prop = bảng không có cột chọn. Cố ý opt-in: `ProductTable`
   * còn dùng ở chỗ khác, mà một cột checkbox không ai bấm chỉ tốn bề ngang.
   */
  selectedIds?: Set<string>;
  onToggleOne?: (id: string) => void;
  onTogglePage?: (ids: string[], nextChecked: boolean) => void;
}

/**
 * Vùng bấm của ô tích.
 *
 * Ô tích thật chỉ 18x18px — quá nhỏ để trỏ trúng, nhất là trên laptop trackpad.
 * Bọc trong `<label>` 40x40px thì bấm ở đâu trong vùng đó cũng tích được, mà
 * ô tích vẫn giữ nguyên kích thước nhìn thấy.
 *
 * `-mx-3 -my-3` kéo vùng bấm lấn 12px vào phần đệm sẵn có của ô bảng
 * (`px-6 py-4`), nên phần chiếm chỗ khi dàn trang vẫn đúng 16px như ô tích cũ:
 * vùng bấm to ra mà hàng KHÔNG cao thêm và cột KHÔNG rộng thêm.
 *
 * `hover:bg-slate-100` để vùng bấm lớn hơn là thứ nhìn thấy được — không thì
 * người dùng vẫn nhắm vào đúng 18px ở giữa như cũ.
 */
const CHECKBOX_HIT_AREA =
  '-my-3 -mx-3 inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg transition-colors hover:bg-slate-100';

const CHECKBOX_INPUT =
  'h-[18px] w-[18px] cursor-pointer rounded border-slate-300 text-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-0';

const createColumns = (
  t: (key: string) => string,
  onEdit?: (item: ProductItem) => void,
  onDelete?: (item: ProductItem) => void,
  onTranslations?: (item: ProductItem) => void,
  selection?: {
    selectedIds: Set<string>;
    pageIds: string[];
    onToggleOne: (id: string) => void;
    onTogglePage: (ids: string[], nextChecked: boolean) => void;
  }
): Column<ProductItem>[] => [
    ...(selection
      ? [
          {
            key: 'select',
            width: '44px',
            /**
             * Ô tích ở header chỉ tác động lên TRANG HIỆN TẠI.
             *
             * Danh sách phân trang ở server (50 dòng/trang), nên "chọn tất cả"
             * theo nghĩa toàn bộ kết quả lọc là một tính năng khác hẳn: phải
             * gửi bộ lọc lên BE thay vì gửi danh sách id. Ô tích này nói đúng
             * phạm vi nó làm được, còn thanh hành động bên dưới ghi rõ số đã
             * chọn để không ai nhầm.
             */
            header: (() => {
              const allOnPage =
                selection.pageIds.length > 0 &&
                selection.pageIds.every((id) => selection.selectedIds.has(id));
              return (
                <label className={CHECKBOX_HIT_AREA} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={allOnPage}
                    onChange={(e) => selection.onTogglePage(selection.pageIds, e.target.checked)}
                    aria-label={t('bulkSelectPage')}
                    className={CHECKBOX_INPUT}
                  />
                </label>
              );
            })(),
            render: (row: ProductItem) => (
              // Chặn `onRowClick` đặt ở LABEL, không phải ở input: bấm vào phần
              // đệm của label thì trình duyệt tự chuyển click sang input, cú
              // click đó nổi bọt qua label — chặn ở đây là chặn được cả hai
              // đường (bấm thẳng ô tích và bấm vùng quanh nó).
              <label className={CHECKBOX_HIT_AREA} onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selection.selectedIds.has(row.id)}
                  onChange={() => selection.onToggleOne(row.id)}
                  aria-label={row.name}
                  className={CHECKBOX_INPUT}
                />
              </label>
            ),
          } as Column<ProductItem>,
        ]
      : []),
    {
      key: 'image',
      header: t('img'),
      width: '80px',
      render: (row) => <ProductCells.ImageCell imageUrl={row.imageUrl} productName={row.name} />,
    },
    {
      key: 'productCode',
      header: t('productCode'),
      width: '130px',
      render: (row) => <ProductCells.ProductCodeCell code={row.productCode} />,
    },
    {
      key: 'barcode',
      header: t('barCode'),
      width: '120px',
      render: (row) => <ProductCells.BarcodeCell barcode={row.barcode} />,
    },
    {
      key: 'name',
      header: t('productName'),
      render: (row) => (
        <ProductCells.ProductNameCell name={row.name} supplier={row.categoryPath} />
      ),
    },
    {
      key: 'price',
      header: t('enterSellingPrice').replace('Enter ', ''), // "Selling Price"
      width: '130px',
      render: (row) => <ProductCells.PriceCell price={row.price} />,
    },
    {
      key: 'costPrice',
      header: t('costPrice'),
      width: '120px',
      render: (row) => <ProductCells.PriceCell price={row.costPrice} />,
    },
    // Bug #52: cột "VAT" đã gỡ. Nó hiển thị 10% cho MỌI sản phẩm vì
    // `mapProductToItem` gán cứng `vatRate: 10` — API sản phẩm không có trường
    // thuế nào (xem `Product` trong shared-types), và popup Add/Edit product
    // cũng không có ô nhập VAT. Một con số bịa giống hệt nhau trên mọi dòng thì
    // tệ hơn là không hiển thị: người dùng tưởng đó là dữ liệu thật.
    //
    // Thuế hiện chỉ tồn tại ở mức TOÀN HỆ THỐNG (`tax_rate` trong
    // GET /settings/public/), không theo từng sản phẩm — mà một cột lặp lại cùng
    // một giá trị ở mọi dòng cũng không mang thông tin gì. Muốn có VAT theo sản
    // phẩm thì BE phải thêm field trước, rồi form mới có ô nhập, rồi mới dựng
    // lại cột này.
    // Cột "Stock" gỡ 09/09/2026: tồn kho chỉ còn hiện ở trang Inventory.
    // Danh sách sản phẩm không có bộ chọn cửa hàng, trong khi tồn luôn thuộc về
    // MỘT cửa hàng — con số đứng đây không trả lời được "tồn của ai", và cũng
    // không sửa được từ màn này. Xem/điều chỉnh tồn ở Inventory.
    {
      key: 'status',
      header: t('status'),
      width: '110px',
      render: (row) => <ProductCells.StatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: t('action'),
      width: '140px',
      align: 'right',
      render: (row) => (
        <ProductCells.ActionButtons
          onEdit={() => onEdit?.(row)}
          onDelete={() => onDelete?.(row)}
          onTranslations={onTranslations ? () => onTranslations(row) : undefined}
        />
      ),
    },
  ];

export function ProductTable({
  data,
  isLoading,
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onRowClick,
  onEdit,
  onDelete,
  onTranslations,
  selectedIds,
  onToggleOne,
  onTogglePage,
}: ProductTableProps) {
  const { t } = useI18n();
  const selection =
    selectedIds && onToggleOne && onTogglePage
      ? {
          selectedIds,
          pageIds: data.map((row) => row.id),
          onToggleOne,
          onTogglePage,
        }
      : undefined;
  const columns = createColumns(t, onEdit, onDelete, onTranslations, selection);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <DataTable
        columns={columns}
        data={data}
        rowKey="id"
        isLoading={isLoading}
        onRowClick={onRowClick}
        showFilter={false}
        showHeaderMenu={false}
        emptyMessage={t('noProductsFoundMatchingFilters')}
        className="border-0 shadow-none"
      />

      {!isLoading && data.length > 0 && (
        <div className="border-t border-slate-100">
          <Pagination
            variant="numbered"
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={onPageChange}
            itemLabel="products"
          />
        </div>
      )}
    </div>
  );
}
